import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Input,
  Button,
  Grid,
  Divider,
  Snackbar,
  CssBaseline,
  Select,
  Option,
  FormLabel,
  IconButton,
  Modal,
  ModalDialog,
  ModalClose,
  Stack,
  Textarea,
  Tooltip,
  CircularProgress,
  Breadcrumbs,
} from '@mui/joy';
import {
  Typography as MuiTypography,
} from '@mui/material';
import {
  Save as SaveIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Security as SecurityIcon,
  KeyboardArrowRight,
} from '@mui/icons-material';
import Link from '@mui/joy/Link';
import { DataGrid } from '@mui/x-data-grid';
import {
  getRoles,
  createRole,
  updateRole,
  deleteRole,
  assignPermissionsToRole,
  getRolePermissions,
  getPermissions,
} from '../../main/API/authentication';
import PermissionGate from '../../main/components/PermissionGate';
import NativeCheckbox from '../../main/components/NativeCheckbox';
import { PERMISSIONS } from '../../main/context/AuthContext';
import { usePermissions } from '../../main/hooks/usePermissions';
import { DirectionalMaterialThemeProvider } from '../../main/theme/DirectionalMaterialThemeProvider';
import DeleteAlert from '../../main/widgets/DeleteAlert';
import RolePermissionsByPageEditor from './widgets/RolePermissionsByPageEditor';
import { getPaginatedOrKeyedResults } from '../../main/utils/pagination';

export default function RolesScreen() {
  const { t } = useTranslation('ui');
  const { hasPermission } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [data, setData] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [filters, setFilters] = useState({
    search: '',
    is_active: true,
    is_system_role: '',
  });
  const [openModal, setOpenModal] = useState(false);
  const [openPermissionsModal, setOpenPermissionsModal] = useState(false);
  const [permissionsModalLoading, setPermissionsModalLoading] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [allPermissions, setAllPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    is_active: true,
    is_system_role: false,
    branch: null,
  });
  
  // Notification states
  const [successNotification, setSuccessNotification] = useState(false);
  const [failedNotification, setFailedNotification] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  
  // Delete alert states
  const [deleteAlert, setDeleteAlert] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState(null);

  const loadRoles = useCallback(async () => {
    if (!hasPermission(PERMISSIONS.ROLES_LIST)) {
      setRoles([]);
      return;
    }

    setLoading(true);
    try {
      const params = {
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
        pagination: true,
        ...filters,
      };
      
      const response = await getRoles(params);
      if (response && response.data) {
        setData(response.data);
        setRoles(response.data.results || []);
      } else {
        setRoles([]);
        setData(null);
      }
    } catch (error) {
      console.error('Failed to load roles:', error);
      setResponseMessage(error.message || t('settingsPages.roles.loadFailed'));
      setFailedNotification(true);
      setRoles([]);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [hasPermission, paginationModel.page, paginationModel.pageSize, filters, t]);

  const loadPermissions = useCallback(async () => {
    if (!hasPermission(PERMISSIONS.PERMISSIONS_LIST)) {
      setAllPermissions([]);
      return [];
    }
    try {
      const response = await getPermissions({ is_active: true, pagination: false });
      const permissions = getPaginatedOrKeyedResults(response && response.data);
      setAllPermissions(permissions);
      return permissions;
    } catch (error) {
      console.error('Failed to load permissions:', error);
      setAllPermissions([]);
      return [];
    }
  }, [hasPermission]);

  useEffect(() => {
    loadRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, paginationModel.pageSize]);

  const handleOpenModal = useCallback((role = null) => {
    if (role) {
      setEditingRole(role);
      setFormData({
        name: role.name || '',
        display_name: role.display_name || '',
        description: role.description || '',
        is_active: role.is_active !== undefined ? role.is_active : true,
        is_system_role: role.is_system_role || false,
        branch: role.branch || null,
      });
    } else {
      setEditingRole(null);
      setFormData({
        name: '',
        display_name: '',
        description: '',
        is_active: true,
        is_system_role: false,
        branch: null,
      });
    }
    setOpenModal(true);
  }, []);

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingRole(null);
  };

  const handleOpenPermissionsModal = useCallback(async (role) => {
    if (!hasPermission(PERMISSIONS.ROLES_VIEW_PERMISSIONS)) {
      setResponseMessage(t('permissions.noPermission', { defaultValue: 'You do not have permission to perform this action.' }));
      setFailedNotification(true);
      return;
    }
    setSelectedRole(role);
    setSelectedPermissions([]);
    setAllPermissions([]);
    setPermissionsModalLoading(true);
    setOpenPermissionsModal(true);
    try {
      const [rolePermsResponse] = await Promise.all([
        getRolePermissions(role.id),
        loadPermissions(),
      ]);
      if (rolePermsResponse && rolePermsResponse.data) {
        const permissions = getPaginatedOrKeyedResults(rolePermsResponse.data, 'permissions');
        setSelectedPermissions(permissions.map((p) => p.id));
      } else {
        setSelectedPermissions([]);
      }
    } catch (error) {
      console.error('Failed to load role permissions:', error);
      setResponseMessage(error.message || t('settingsPages.common.loadFailed'));
      setFailedNotification(true);
      setOpenPermissionsModal(false);
      setSelectedRole(null);
    } finally {
      setPermissionsModalLoading(false);
    }
  }, [hasPermission, loadPermissions, t]);

  const handleClosePermissionsModal = () => {
    setOpenPermissionsModal(false);
    setPermissionsModalLoading(false);
    setSelectedRole(null);
    setSelectedPermissions([]);
  };

  const handleSave = async () => {
    if (editingRole ? !hasPermission(PERMISSIONS.ROLES_UPDATE) : !hasPermission(PERMISSIONS.ROLES_CREATE)) {
      setResponseMessage(t('permissions.noPermission', { defaultValue: 'You do not have permission to perform this action.' }));
      setFailedNotification(true);
      return;
    }
    try {
      setLoading(true);
      if (editingRole) {
        await updateRole(editingRole.id, formData);
        setResponseMessage(t('settingsPages.roles.updatedSuccess'));
      } else {
        await createRole(formData);
        setResponseMessage(t('settingsPages.roles.createdSuccess'));
      }
      setSuccessNotification(true);
      handleCloseModal();
      loadRoles();
    } catch (error) {
      console.error('Failed to save role:', error);
      setResponseMessage(error.message || t('settingsPages.roles.saveFailed'));
      setFailedNotification(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePermissions = async () => {
    if (!selectedRole) return;
    if (!hasPermission(PERMISSIONS.ROLES_ASSIGN_PERMISSIONS)) {
      setResponseMessage(t('permissions.noPermission', { defaultValue: 'You do not have permission to perform this action.' }));
      setFailedNotification(true);
      return;
    }

    try {
      setLoading(true);
      await assignPermissionsToRole(selectedRole.id, selectedPermissions);
      setResponseMessage(t('settingsPages.roles.permsUpdatedSuccess'));
      setSuccessNotification(true);
      handleClosePermissionsModal();
      loadRoles();
    } catch (error) {
      console.error('Failed to assign permissions:', error);
      setResponseMessage(error.message || t('settingsPages.roles.permsUpdateFailed'));
      setFailedNotification(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = useCallback((id) => {
    setSelectedRoleId(id);
    setDeleteAlert(true);
  }, []);

  const confirmDelete = async () => {
    if (!selectedRoleId) return;
    if (!hasPermission(PERMISSIONS.ROLES_DELETE)) {
      setResponseMessage(t('permissions.noPermission', { defaultValue: 'You do not have permission to perform this action.' }));
      setFailedNotification(true);
      return;
    }
    
    try {
      setDeleteLoading(true);
      await deleteRole(selectedRoleId);
      setResponseMessage(t('settingsPages.roles.deletedSuccess'));
      setSuccessNotification(true);
      setDeleteAlert(false);
      setSelectedRoleId(null);
      loadRoles();
    } catch (error) {
      console.error('Failed to delete role:', error);
      setResponseMessage(error.message || t('settingsPages.roles.deleteFailed'));
      setFailedNotification(true);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    loadRoles();
  };

  const handlePageSizeChange = (pageSize) => {
    setPaginationModel({ page: 0, pageSize });
  };

  const handlePageChange = (page) => {
    setPaginationModel((prev) => ({ ...prev, page }));
  };

  const columns = useMemo(
    () => [
      { field: 'id', headerName: t('settingsPages.roles.colId'), flex: 1, minWidth: 80 },
      { field: 'display_name', headerName: t('settingsPages.roles.colName'), flex: 2, minWidth: 180 },
      { field: 'name', headerName: t('settingsPages.permissions.colCodename'), flex: 2, minWidth: 150 },
      {
        field: 'permission_count',
        headerName: t('settingsPages.roles.colPermissions'),
        flex: 1.5,
        minWidth: 120,
        renderCell: (params) =>
          t('settingsPages.roles.permissionsCount', { count: params.row.permission_count || 0 }),
      },
      {
        field: 'is_active',
        headerName: t('settingsPages.permissions.colStatus'),
        flex: 1,
        minWidth: 100,
        renderCell: (params) => (
          <Typography
            sx={{
              color: params.row.is_active ? 'success.main' : 'neutral.main',
              fontWeight: 500,
            }}
          >
            {params.row.is_active
              ? t('settingsPages.permissions.statusActive')
              : t('settingsPages.permissions.statusInactive')}
          </Typography>
        ),
      },
      {
        field: 'is_system_role',
        headerName: t('settingsPages.roles.labelSystemRole'),
        flex: 1,
        minWidth: 100,
        renderCell: (params) => (
          <Typography
            sx={{
              color: params.row.is_system_role ? 'warning.main' : 'neutral.main',
              fontWeight: 500,
            }}
          >
            {params.row.is_system_role ? t('settingsPages.users.yes') : t('settingsPages.users.no')}
          </Typography>
        ),
      },
      {
        field: 'actions',
        headerName: t('settingsPages.roles.colActions'),
        flex: 2,
        minWidth: 150,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {hasPermission(PERMISSIONS.ROLES_VIEW_PERMISSIONS) && hasPermission(PERMISSIONS.ROLES_ASSIGN_PERMISSIONS) && (
              <Tooltip title={t('settingsPages.roles.tooltipManagePerms')}>
                <IconButton
                  size="sm"
                  variant="soft"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenPermissionsModal(params.row);
                  }}
                >
                  <SecurityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {hasPermission(PERMISSIONS.ROLES_UPDATE) && (
              <Tooltip title={t('settingsPages.roles.tooltipEdit')}>
                <IconButton
                  size="sm"
                  variant="soft"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenModal(params.row);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {hasPermission(PERMISSIONS.ROLES_DELETE) && (
              <Tooltip title={t('settingsPages.roles.tooltipDelete')}>
                <IconButton
                  size="sm"
                  variant="soft"
                  color="danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(params.row.id);
                  }}
                  disabled={params.row.is_system_role}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    ],
    [t, hasPermission, handleOpenPermissionsModal, handleOpenModal, handleDelete]
  );

  const handleSelectAllPermissions = () => {
    setSelectedPermissions(allPermissions.map(perm => perm.id));
  };

  const handleDeselectAllPermissions = () => {
    setSelectedPermissions([]);
  };

  const allPermissionsSelected =
    allPermissions.length > 0 &&
    allPermissions.every(perm => selectedPermissions.includes(perm.id));

  return (
    <DirectionalMaterialThemeProvider>
      <CssBaseline />
      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex' }} m={3}>
          <Box width={"100%"}>
            <Box sx={{ mb: 3 }}>
              <Breadcrumbs separator={<KeyboardArrowRight />} aria-label="breadcrumbs">
                <Link color="neutral" href="/settings">
                  {t('settingsPages.roles.breadcrumbSettings')}
                </Link>
                <Typography level="body-md">{t('settingsPages.roles.breadcrumbCurrent')}</Typography>
              </Breadcrumbs>
              <Typography textAlign={"start"} level="h1">{t('settingsPages.roles.pageTitle')}</Typography>
            </Box>

            <PermissionGate permissions={[PERMISSIONS.ROLES_LIST]}>
              {/* Filters */}
              <Grid container spacing={1} sx={{ flexGrow: 1, mb: 1 }}>
                <Grid item xs={12} sm={6} md={3} lg={3}>
                  <Input
                    placeholder={t('settingsPages.roles.searchPlaceholder')}
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    startDecorator={<SearchIcon />}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={3}>
                  <Select
                    value={filters.is_active === '' ? 'all' : (filters.is_active ? 'active' : 'inactive')}
                    onChange={(e, value) => setFilters(prev => ({ ...prev, is_active: value === 'all' ? '' : value === 'active' }))}
                  >
                    <Option value="all">{t('settingsPages.permissions.filterAllStatus')}</Option>
                    <Option value="active">{t('settingsPages.permissions.statusActive')}</Option>
                    <Option value="inactive">{t('settingsPages.permissions.statusInactive')}</Option>
                  </Select>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={3}>
                  <Select
                    value={filters.is_system_role === '' ? 'all' : (filters.is_system_role ? 'yes' : 'no')}
                    onChange={(e, value) => setFilters(prev => ({ ...prev, is_system_role: value === 'all' ? '' : value === 'yes' }))}
                  >
                    <Option value="all">{t('settingsPages.roles.filterSystemAll')}</Option>
                    <Option value="yes">{t('settingsPages.roles.filterSystem')}</Option>
                    <Option value="no">{t('settingsPages.roles.filterCustom')}</Option>
                  </Select>
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={3}>
                  <Button type="submit" loading={loading} startDecorator={<SearchIcon />}>
                    {t('actions.search')}
                  </Button>
                </Grid>
              </Grid>

              {/* Actions icons */}
              <Grid mt={2} container spacing={2} justifyContent="flex-end">
                <Grid
                  container
                  spacing={1}
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'flex-start'
                  }}
                >
                  <PermissionGate permissions={[PERMISSIONS.ROLES_CREATE]}>
                    <Grid item>
                      <Tooltip title={t('settingsPages.roles.modalCreateTitle')}>
                        <IconButton
                          onClick={() => handleOpenModal()}
                          color="primary"
                          size="large"
                          sx={{
                            mr: { xs: 0, md: 1 },
                            width: { xs: '100%', md: 'auto' }
                          }}
                        >
                          <AddIcon />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </PermissionGate>
                </Grid>
              </Grid>

              {/* Table */}
              <Box>
                <DataGrid
                  rows={roles || []}
                  columns={columns}
                  loading={loading}
                  pagination
                  paginationModel={paginationModel}
                  paginationMode="server"
                  rowCount={data ? data.count : 0}
                  onPaginationModelChange={(params) => {
                    if (params.pageSize !== paginationModel.pageSize) {
                      handlePageSizeChange(params.pageSize);
                    } else {
                      handlePageChange(params.page);
                    }
                  }}
                  pageSizeOptions={[10, 25, 50, 100]}
                  hideFooterSelectedRowCount
                  getRowHeight={() => 'auto'}
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    '& .MuiDataGrid-root': {
                      border: 'none',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: '#f5f5f5',
                      borderBottom: '2px solid #e0e0e0',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#333',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                      '& .MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 600,
                        fontSize: '14px',
                        color: '#333',
                      },
                    },
                    '& .MuiDataGrid-cell': {
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                      lineHeight: '1.5',
                      padding: '12px 16px',
                      fontSize: '14px',
                      color: '#555',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                      borderBottom: '1px solid #f0f0f0',
                    },
                    '& .MuiDataGrid-row': {
                      '&:hover': {
                        backgroundColor: '#f8f9fa',
                        cursor: 'pointer',
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#e3f2fd',
                        '&:hover': {
                          backgroundColor: '#bbdefb',
                        },
                      },
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: '2px solid #e0e0e0',
                      backgroundColor: '#fafafa',
                      fontSize: '14px',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    },
                    '& .MuiDataGrid-toolbarContainer': {
                      padding: '12px',
                      backgroundColor: '#fafafa',
                      borderBottom: '1px solid #e0e0e0',
                    },
                    '@media (max-width: 600px)': {
                      '& .MuiDataGrid-toolbarContainer': {
                        flexDirection: 'column',
                        gap: 1,
                      },
                      '& .MuiDataGrid-footer': {
                        flexDirection: 'column',
                        gap: 1,
                      },
                      '& .MuiDataGrid-cell': {
                        minHeight: '60px',
                        alignItems: 'center',
                        padding: '8px',
                        fontSize: '13px',
                      },
                      '& .MuiDataGrid-columnHeaders': {
                        fontSize: '13px',
                      },
                    }
                  }}
                />
              </Box>
            </PermissionGate>

            {/* Create/Edit Role Modal */}
            <Modal open={openModal} onClose={handleCloseModal}>
              <ModalDialog sx={{ maxWidth: 600, width: '100%' }}>
                <ModalClose />
                <Typography level="h4" sx={{ mb: 2 }}>
                  {editingRole ? t('settingsPages.roles.modalEditTitle') : t('settingsPages.roles.modalCreateTitle')}
                </Typography>
                <Stack spacing={2}>
                  <FormLabel>{t('settingsPages.permissions.labelCodename')} *</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="role_name"
                    disabled={!!editingRole}
                  />
                  
                  <FormLabel>{t('settingsPages.roles.colName')} *</FormLabel>
                  <Input
                    value={formData.display_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                    placeholder="Role Display Name"
                  />
                  
                  <FormLabel>{t('settingsPages.roles.labelDescription')}</FormLabel>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Role description"
                    minRows={3}
                  />
                  
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <NativeCheckbox
                      label={t('settingsPages.permissions.labelActive')}
                      checked={formData.is_active}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    />
                    {/* <Checkbox
                      label="System Role"
                      checked={formData.is_system_role}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_system_role: e.target.checked }))}
                    /> */}
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                    <Button variant="outlined" onClick={handleCloseModal}>
                      {t('actions.cancel')}
                    </Button>
                    <PermissionGate permissions={editingRole ? [PERMISSIONS.ROLES_UPDATE] : [PERMISSIONS.ROLES_CREATE]}>
                      <Button
                        onClick={handleSave}
                        disabled={loading || !formData.name || !formData.display_name}
                        startDecorator={loading ? <CircularProgress size="sm" /> : <SaveIcon />}
                      >
                        {loading ? t('settingsPages.common.saving') : t('settingsPages.roles.save')}
                      </Button>
                    </PermissionGate>
                  </Box>
                </Stack>
              </ModalDialog>
            </Modal>

            {/* Permissions Assignment Modal */}
            <Modal open={openPermissionsModal} onClose={handleClosePermissionsModal}>
              <ModalDialog
                sx={{
                  maxWidth: 1100,
                  width: '95vw',
                  maxHeight: '95vh',
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0,
                }}
              >
                <ModalClose />
                <Box sx={{ mb: 1.5 }}>
                  <Typography level="h4" sx={{ mb: 0.5 }}>
                    {t('settingsPages.roles.modalPermsTitle')}
                    {selectedRole && selectedRole.display_name
                      ? `: ${selectedRole.display_name}`
                      : ''}
                  </Typography>
                  <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                    {t('settingsPages.rolesPermsEditor.intro', {
                      defaultValue:
                        'Grant access page-by-page. Permissions shared across multiple pages are flagged.',
                    })}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                    pr: 0.5,
                    mr: -0.5,
                  }}
                >
                  {permissionsModalLoading ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 280,
                        gap: 2,
                      }}
                    >
                      <CircularProgress size="lg" />
                      <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                        {t('actions.loading')}
                      </Typography>
                    </Box>
                  ) : (
                    <RolePermissionsByPageEditor
                      allPermissions={allPermissions}
                      selectedPermissionIds={selectedPermissions}
                      onChange={setSelectedPermissions}
                    />
                  )}
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pt: 1.5,
                    mt: 1.5,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    flexWrap: 'wrap',
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {!permissionsModalLoading && (allPermissionsSelected ? (
                      <Button
                        size="sm"
                        variant="outlined"
                        color="danger"
                        onClick={handleDeselectAllPermissions}
                      >
                        {t('settingsPages.roles.deselectAll')}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outlined"
                        color="primary"
                        onClick={handleSelectAllPermissions}
                      >
                        {t('settingsPages.roles.selectAll')}
                      </Button>
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" color="neutral" onClick={handleClosePermissionsModal}>
                      {t('actions.cancel')}
                    </Button>
                    <PermissionGate permissions={[PERMISSIONS.ROLES_ASSIGN_PERMISSIONS]}>
                      <Button
                        onClick={handleSavePermissions}
                        disabled={loading || permissionsModalLoading}
                        startDecorator={loading ? <CircularProgress size="sm" /> : <SaveIcon />}
                      >
                        {loading ? t('settingsPages.common.saving') : t('settingsPages.roles.savePerms')}
                      </Button>
                    </PermissionGate>
                  </Box>
                </Box>
              </ModalDialog>
            </Modal>

            {/* Notifications */}
            <Snackbar
              autoHideDuration={3000}
              open={successNotification}
              variant="soft"
              color="success"
              onClose={() => setSuccessNotification(false)}
            >
              {responseMessage}
            </Snackbar>

            <Snackbar
              autoHideDuration={3000}
              open={failedNotification}
              variant="soft"
              color="danger"
              onClose={() => setFailedNotification(false)}
            >
              {responseMessage}
            </Snackbar>

            <DeleteAlert
              label={t('nav.roles')}
              open={deleteAlert}
              onDelete={confirmDelete}
              onClose={() => {
                setDeleteAlert(false);
                setSelectedRoleId(null);
              }}
              loading={deleteLoading}
            />
          </Box>
        </Box>
      </form>
    </DirectionalMaterialThemeProvider>
  );
}

